import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import { Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { IImpersonationContextComponentProps, ILocationState } from '../../../../models';
import { BlogPostRequest } from '../../../../models/Blogs';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { MediaChooserContext, useMediaChooserContextController } from '../../../../web/components/MediaChooser/context';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { BlogPostContextProvider } from '../../../../web/containers/blogs/blogPostContext';
import { CreateBlogPost } from '../../../../web/containers/blogs/Post/CreateBlogPost';
import { CreateBlogPostFromTemplate } from '../../../../web/containers/blogs/Post/CreateBlogPostFromTemplate';
import { EditBlogPost } from '../../../../web/containers/blogs/Post/EditBlogPost';
import { ScheduleBlogPost } from '../../../../web/containers/blogs/ScheduleBlogPost';
import { AccountContentCalendarBlogScheduleSuccess } from '../AccountContentCalendarBlogScheduleSuccess';

export const AccountContentCalendarBlogRoute = observer(function AccountContentCalendarBlogRoute() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const mediaContext = useMediaChooserContextController();
	const { url } = useRouteMatch();
	const loc = useLocation<ILocationState<any, BlogPostRequest>>();
	const impersonationProviderProps = React.useRef<IImpersonationContextComponentProps>({
		impersonationContext: new ImpersonationContextViewModel(loc.state?.model?.impersonationContext),
	}).current;

	const dismissModal = () => {
		fullscreenModal.dismissModal?.();
		return true;
	};

	const onScheduleClicked = () => {
		fullscreenModal.history.push(`${url}/post/schedule`);
		return true;
	};

	return (
		<Provider {...impersonationProviderProps}>
			<BlogPostContextProvider>
				<Switch>
					<PrivateRoute path={`${url}/post/schedule`} userSession={userSession}>
						<ScheduleBlogPost
							onCreated={dismissModal}
							onScheduled={() => {
								fullscreenModal.history.push(`${url}/post/success`);
								return true;
							}}
							onUpdated={() => {
								fullscreenModal.history.goBack();
								return true;
							}}
						/>
					</PrivateRoute>
					<PrivateRoute path={`${url}/post/success`} userSession={userSession}>
						<AccountContentCalendarBlogScheduleSuccess />
					</PrivateRoute>
					<PrivateRoute path={`${url}/post/create`} userSession={userSession}>
						<MediaChooserContext.Provider value={mediaContext}>
							<CreateBlogPost onScheduleClicked={onScheduleClicked} />
						</MediaChooserContext.Provider>
					</PrivateRoute>
					<PrivateRoute path={`${url}/post/edit/:id`} userSession={userSession}>
						<EditBlogPost onScheduleClicked={onScheduleClicked} />
					</PrivateRoute>
					<PrivateRoute path={`${url}/post/create-from-template/:id`} userSession={userSession}>
						<CreateBlogPostFromTemplate onScheduleClicked={onScheduleClicked} />
					</PrivateRoute>
				</Switch>
			</BlogPostContextProvider>
		</Provider>
	);
});
